import { Badge } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type AddMedicationSummaryProps = {
  medicationName?: string
  isControlledDrug: boolean
  /** Unset until the user picks scheduled or PRN. */
  isPrn?: boolean
}

/** The medication being added, under the stepper once one is picked. */
export const AddMedicationSummary = ({
  medicationName,
  isControlledDrug,
  isPrn
}: AddMedicationSummaryProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  if (!medicationName) {
    return null
  }

  const items: Array<{ key: string; content: ReactNode }> = [
    { key: 'name', content: <span>{medicationName}</span> }
  ]

  if (isControlledDrug) {
    items.push({
      key: 'controlledDrug',
      content: (
        <Group>
          {/* The label beside it already says it */}
          <Badge colour="red" aria-hidden>
            {t(emarV2Constants.CD)}
          </Badge>
          {t(emarV2Constants.ADD_MEDICATION_CONTROLLED_DRUG)}
        </Group>
      )
    })
  }

  if (isPrn !== undefined) {
    items.push({
      key: 'schedule',
      content: isPrn ? (
        <Group $gap={spacing[100]}>
          {t(emarV2Constants.ADD_MEDICATION_NON_SCHEDULED)}
          <Badge colour="blue">{t(emarV2Constants.PRN)}</Badge>
        </Group>
      ) : (
        <span>{t(emarV2Constants.SCHEDULED)}</span>
      )
    })
  }

  return (
    <Summary>
      {items.map(({ key, content }, index) => (
        <Fragment key={key}>
          {index > 0 && <span aria-hidden>•</span>}
          {content}
        </Fragment>
      ))}
    </Summary>
  )
}

const { color, fontWeight, size, spacing, text } = cssVars

/** 12px text on a 16px line, as designed; the body-xs token's line is 15px. */
const Summary = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[100]};
  margin: 0;
  font-size: ${text.body.xs.size};
  font-weight: ${fontWeight.medium};
  line-height: ${size[200]};
  color: ${color.text.secondary};
`

const Group = styled.span<{ $gap?: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $gap = spacing[50] }) => $gap};
`
